import { A11yModule } from '@angular/cdk/a11y';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AuthDrawerComponent } from 'src/app/components/auth-drawer/auth-drawer.component';
import { IconModule } from "src/app/icon/icon.module";
import { MaterialModule } from 'src/app/material.module';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { PATTERN, ROUTES } from 'src/app/shared/constants';
import { clearServerErrors, generateSecurityCode, securityCodeValidator } from 'src/app/shared/utils';

@Component({
  selector: 'app-forgot-password',
  imports: [A11yModule, ReactiveFormsModule, TranslateModule, IconModule, MaterialModule, AuthDrawerComponent],
  templateUrl: './forgot-password.component.html'
})
export class ForgotPasswordComponent {
  forgotPasswordForm: FormGroup;
  spinLoader = false;
  securityCode = generateSecurityCode();

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private toastService: ToastService,
    private translateService: TranslateService,
    private authService: AuthService) {
      this.initForm();
  }

  initForm() {
    this.forgotPasswordForm = this.formBuilder.group({
      email: [null, [Validators.required, Validators.pattern(PATTERN.EMAIL)]],
      securityCode: [null, [Validators.required, securityCodeValidator(() => this.securityCode)]]
    });
  }

  refreshSecurityCode() {
    this.securityCode = generateSecurityCode();
    this.forgotPasswordForm.get('securityCode')?.reset();
  }

  isInvalid(key: string) {
    const control = this.forgotPasswordForm.get(key);
    return !!control && control.invalid && control.touched;
  }

  hasError(key: string, errorType: string) {
    return !!this.forgotPasswordForm.get(key)?.hasError(errorType);
  }

  close() {
    this.router.navigate([ROUTES.USER.LOGIN]);
  }

  onSubmit() {
    clearServerErrors(this.forgotPasswordForm);

    if (this.forgotPasswordForm.invalid) return this.forgotPasswordForm.markAllAsTouched();
    this.spinLoader = true;
    const redirectUrl = `${window.location.origin}/reset-password`;
    this.authService
      .forgotPassword({ email: this.forgotPasswordForm.value.email, redirectUrl })
      .pipe(finalize(() => (this.spinLoader = false)))
      .subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('password reset request sent'));
          this.close();
        },
        error: ({ error, status }) => {
          this.refreshSecurityCode();
          if (!error)
            return;

          switch (status) {
            case 400:
              this.forgotPasswordForm.get('email')?.setErrors({ serverError: error });
              break;
            case 404:
            case 429:
              error?.Message && this.toastService.warning(this.translateService.instant(error?.Message), undefined, { timeOut: 5000 });
              break;
            default:
              this.toastService.error(this.translateService.instant(error.Message));
              break;
          }
        }
      });
  }
}
